import { InputSampleValues, Sample } from '../../@types/samples/types';

export type PcCorrectionStatus = 'ready' | 'empty' | 'invalid';

export interface PcCorrectionRow {
  key: string;
  index: number;
  sw: number | null;
  pc: number | null;
  correctedSw: number | null;
  correctedPc: number | null;
  pres: number | null;
  pcD: number | null;
  swD: number | null;
  status: PcCorrectionStatus;
  statusLabel: string;
}

interface BuildPcCorrectionRowsInput {
  sw: Array<number | null>;
  pc: Array<number | null>;
  inputSampleValues: InputSampleValues;
  iftCosLab: number | null;
  iftCosRes: number | null;
}

export interface PcCorrectionBreakdown {
  salinity: number;
  salinityIsDefault: boolean;
  qv: number;
  qvIsDefault: boolean;
  phiRes: number;
  phiResIsDefault: boolean;
  fValue: number | null;
  iftCosLab: number | null;
  iftCosRes: number | null;
  pressureMultiplier: number | null;
  missingMessage: string | null;
}

const round = (value: number, precision = 4) => {
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
};

const finiteOrNull = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const isPositive = (value: number | null): value is number => value !== null && value > 0;

const displayStatus = (status: PcCorrectionStatus, message?: string | null) =>
  status === 'ready' ? 'Ready' : message || 'Needs input';

export const parseMeasuredValue = (value: string): number | null => {
  if (!value.trim()) return null;
  const parsed = Number(value.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

export const formatPcCorrectionValue = (value: number | null): string =>
  value === null ? '-' : String(value);

export function buildPcCorrectionBreakdown(input: Omit<BuildPcCorrectionRowsInput, 'sw' | 'pc'>): PcCorrectionBreakdown {
  const salinityRaw = finiteOrNull(input.inputSampleValues.salinity);
  const qvRaw = finiteOrNull(input.inputSampleValues.qv);
  const phiResRaw = finiteOrNull(input.inputSampleValues.phi_res);
  const salinity = salinityRaw ?? 140;
  const qv = qvRaw ?? 0;
  const phiRes = phiResRaw ?? 1;
  const iftCosLab = finiteOrNull(input.iftCosLab);
  const iftCosRes = finiteOrNull(input.iftCosRes);
  const fValue = finiteOrNull(1 - 0.6425 * (Math.pow(salinity, -0.5) + 0.22) * qv);
  const pressureMultiplier = isPositive(iftCosLab) && isPositive(iftCosRes)
    ? round(iftCosRes / iftCosLab)
    : null;

  const missingMessage =
    !isPositive(salinity) ? 'Invalid salinity'
      : !isPositive(phiRes) ? 'Invalid PhiRes/PhiLab factor'
        : !isPositive(iftCosLab) || !isPositive(iftCosRes) ? 'Missing conversion parameters'
          : !isPositive(fValue) ? 'Invalid correction factor'
            : null;

  return {
    salinity,
    salinityIsDefault: salinityRaw === null,
    qv,
    qvIsDefault: qvRaw === null,
    phiRes,
    phiResIsDefault: phiResRaw === null,
    fValue: fValue === null ? null : round(fValue),
    iftCosLab,
    iftCosRes,
    pressureMultiplier,
    missingMessage
  };
}

export function buildPcCorrectionRows(input: BuildPcCorrectionRowsInput): PcCorrectionRow[] {
  const rowCount = Math.max(input.sw.length, input.pc.length);
  const salinity = finiteOrNull(input.inputSampleValues.salinity) ?? 140;
  const qv = finiteOrNull(input.inputSampleValues.qv) ?? 0;
  const phiRes = finiteOrNull(input.inputSampleValues.phi_res) ?? 1;
  const iftCosLab = finiteOrNull(input.iftCosLab);
  const iftCosRes = finiteOrNull(input.iftCosRes);
  const validSw = input.sw.filter((value): value is number => value !== null && value >= 0 && value <= 1);
  const swir = finiteOrNull(input.inputSampleValues.swi) ?? (validSw.length ? Math.min(...validSw) : null);
  const fValue = finiteOrNull(1 - 0.6425 * (Math.pow(salinity, -0.5) + 0.22) * qv);

  const globalMessage =
    !isPositive(salinity) ? 'Invalid salinity'
      : !isPositive(phiRes) ? 'Invalid PhiRes/PhiLab factor'
        : !isPositive(iftCosLab) || !isPositive(iftCosRes) ? 'Missing conversion parameters'
          : !isPositive(fValue) ? 'Invalid correction factor'
            : swir !== null && 1 - swir <= 0 ? 'Invalid Swi'
              : null;

  const rows = Array.from({ length: rowCount }, (_, index): PcCorrectionRow => {
    const sw = input.sw[index] ?? null;
    const pc = input.pc[index] ?? null;

    if (sw === null && pc === null) {
      return {
        key: `pc-correction-${index}`,
        index,
        sw,
        pc,
        correctedSw: null,
        correctedPc: null,
        pres: null,
        pcD: null,
        swD: null,
        status: 'empty',
        statusLabel: ''
      };
    }

    const rowMessage =
      sw === null || pc === null ? 'Missing measured value'
        : sw < 0 || sw > 1 ? 'Sw must be between 0 and 1'
          : pc < 0 ? 'Pc must be non-negative'
            : globalMessage;

    if (rowMessage || fValue === null || iftCosLab === null || iftCosRes === null || swir === null) {
      return {
        key: `pc-correction-${index}`,
        index,
        sw,
        pc,
        correctedSw: null,
        correctedPc: null,
        pres: null,
        pcD: null,
        swD: null,
        status: 'invalid',
        statusLabel: displayStatus('invalid', rowMessage)
      };
    }

    const swValue = sw;
    const pcValue = pc;
    if (swValue === null || pcValue === null) {
      return {
        key: `pc-correction-${index}`,
        index,
        sw,
        pc,
        correctedSw: null,
        correctedPc: null,
        pres: null,
        pcD: null,
        swD: null,
        status: 'invalid',
        statusLabel: displayStatus('invalid', 'Missing measured value')
      };
    }

    const correctedSw = round(1 - (1 - (1 - ((1 - swValue) * phiRes))) * fValue);
    const correctedPc = round(pcValue * Math.pow(phiRes, -0.5) * Math.pow(fValue, -0.5));
    const pres = round((correctedPc * iftCosRes) / iftCosLab);

    if (correctedSw < 0 || correctedSw > 1 || pres < 0) {
      return {
        key: `pc-correction-${index}`,
        index,
        sw,
        pc,
        correctedSw: null,
        correctedPc: null,
        pres: null,
        pcD: null,
        swD: null,
        status: 'invalid',
        statusLabel: displayStatus('invalid', 'Corrected value outside valid range')
      };
    }

    return {
      key: `pc-correction-${index}`,
      index,
      sw,
      pc,
      correctedSw,
      correctedPc,
      pres,
      pcD: null,
      swD: null,
      status: 'ready',
      statusLabel: displayStatus('ready')
    };
  });

  const validPres = rows
    .map(row => row.pres)
    .filter((value): value is number => value !== null && value >= 0);
  const pcMax = validPres.length ? Math.max(...validPres) : null;

  return rows.map(row => {
    if (
      row.status !== 'ready' ||
      row.pres === null ||
      row.correctedSw === null ||
      pcMax === null ||
      pcMax <= 0 ||
      swir === null ||
      1 - swir <= 0
    ) {
      return row;
    }

    return {
      ...row,
      pcD: Math.abs(round(row.pres / pcMax)),
      swD: Math.abs(round((row.correctedSw - swir) / (1 - swir)))
    };
  });
}

export function buildSamplePcCorrectionBreakdown(sample: Sample) {
  return buildPcCorrectionBreakdown({
    inputSampleValues: sample.input_sample_values,
    iftCosLab: sample.conversion_parameters?.ift_cos_lab ?? sample.ift_cos_lab ?? null,
    iftCosRes: sample.conversion_parameters?.ift_cos_res ?? sample.ift_cos_res ?? null
  });
}

export function buildSamplePcCorrectionRows(sample: Sample, sw: Array<number | null>, pc: Array<number | null>) {
  return buildPcCorrectionRows({
    sw,
    pc,
    inputSampleValues: sample.input_sample_values,
    iftCosLab: sample.conversion_parameters?.ift_cos_lab ?? sample.ift_cos_lab ?? null,
    iftCosRes: sample.conversion_parameters?.ift_cos_res ?? sample.ift_cos_res ?? null
  });
}
